import useOKRConfig from 'components/hooks/useOKRConfig';
import { intersection } from 'lodash';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';

import { GOAL_VISIBILITY_OPTIONS, OBJECTIVE_TYPES_MAP, OKR_TYPES } from 'constants/ProjectConstants';

export const CREATE_OBJECTIVE_PRIVILEGES = {
  CREATE_OBJECTIVE_INDIVIDUAL: 'CREATE_OBJECTIVE_INDIVIDUAL',
  CREATE_OBJECTIVE_TEAM: 'CREATE_OBJECTIVE_TEAM',
  CREATE_OBJECTIVE_DEPARTMENT: 'CREATE_OBJECTIVE_DEPARTMENT',
  CREATE_OBJECTIVE_COMPANY: 'CREATE_OBJECTIVE_COMPANY',
};

export const VIEW_COMMENT_OBJECTIVE_PRIVILEGES = {
  VIEW_COMMENT_PUBLIC_OBJECTIVES: 'VIEW_COMMENT_PUBLIC_OBJECTIVES',
  VIEW_COMMENT_ALL_OBJECTIVES: 'VIEW_COMMENT_ALL_OBJECTIVES',
};

export const UPDATE_OBJECTIVE_STATUS_PROGRESS_PRIVILEGES = {
  UPDATE_ALL_OBJECTIVE_STATUS_PROGRESS: 'UPDATE_ALL_OBJECTIVE_STATUS_PROGRESS',
  UPDATE_PUBLIC_OBJECTIVE_STATUS_PROGRESS: 'UPDATE_PUBLIC_OBJECTIVE_STATUS_PROGRESS',
};

export const EDIT_OBJECTIVE_PRIVILEGES = {
  EDIT_PUBLIC_OBJECTIVE: 'EDIT_PUBLIC_OBJECTIVE',
  EDIT_ALL_OBJECTIVE: 'EDIT_ALL_OBJECTIVE',
};

export const DELETE_OBJECTIVE_PRIVILEGES = {
  DELETE_ALL_OBJECTIVE: 'DELETE_ALL_OBJECTIVE',
  DELETE_PUBLIC_OBJECTIVE: 'DELETE_PUBLIC_OBJECTIVE',
};

export const CREATE_KEY_RESULT_PRIVILEGES = {
  CREATE_KEY_RESULT_PARENT_MANDATORY: 'CREATE_KEY_RESULT_PARENT_MANDATORY',
  CREATE_KEY_RESULT_PARENT_NON_MANDATORY: 'CREATE_KEY_RESULT_PARENT_NON_MANDATORY',
};

export const VIEW_COMMENT_KEY_RESULTS_PRIVILEGES = {
  VIEW_COMMENT_ALL_KEY_RESULTS: 'VIEW_COMMENT_ALL_KEY_RESULTS',
  VIEW_COMMENT_PUBLIC_KEY_RESULTS: 'VIEW_COMMENT_PUBLIC_KEY_RESULTS',
};

export const UPDATE_KEY_RESULT_STATUS_PROGRESS_PRIVILEGES = {
  UPDATE_PUBLIC_KEY_RESULT_STATUS_PROGRESS: 'UPDATE_PUBLIC_KEY_RESULT_STATUS_PROGRESS',
  UPDATE_ALL_KEY_RESULT_STATUS_PROGRESS: 'UPDATE_ALL_KEY_RESULT_STATUS_PROGRESS',
};

export const EDIT_KEY_RESULT_PRIVILEGES = {
  EDIT_PUBLIC_KEY_RESULT: 'EDIT_PUBLIC_KEY_RESULT',
  EDIT_ALL_KEY_RESULT: 'EDIT_ALL_KEY_RESULT',
};

export const DELETE_KEY_RESULT_PRIVILEGES = {
  DELETE_PUBLIC_KEY_RESULT: 'DELETE_PUBLIC_KEY_RESULT',
  DELETE_ALL_KEY_RESULT: 'DELETE_ALL_KEY_RESULT',
};

export const useOKRAccessControl = (okr = {}) => {
  const krFromOKRDrawer = useSelector(kRDrawerSelector);

  const accessPrivileges = okr?.accessPrivileges ?? krFromOKRDrawer?.accessPrivileges;
  const visibilityType = okr?.visibilityType ?? krFromOKRDrawer?.visibilityType;
  const type = okr?.type ?? krFromOKRDrawer?.type;
  const objectiveLevel = okr?.objectiveType ?? krFromOKRDrawer?.objectiveLevel;
  /* 
    The expectation here is that is okr is present, (i.e. ag-grid), we will set parentOKRType as undefined,
    this is done because we need to handle null and undefined separately, if null that means no parent, 
    if undefined that means don't show clone option all together.

    Ag grid is not sending parentPost info that is why we are doing things this way. once they send it we can
    show clone option in kebab menu
  */
  const parentOKRType = okr?.uuid
    ? undefined
    : krFromOKRDrawer?.parentPost === null
      ? null
      : krFromOKRDrawer?.parentPost?.type;

  const { IS_KR_CASCADING_ENABLED, OBJECTIVE_TYPE } = useOKRConfig();

  const isObjectiveDisabled = OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DISABLED.value;

  /* changing accessPrivilegesList to use accessPrivileges as it is as per api change */
  const accessPrivilegesList = Array.isArray(accessPrivileges) ? accessPrivileges : [];

  /* Key result helpers */

  /* if a child key result can be created. */
  const hasCreateChildKeyResultPrivilege = accessPrivilegesList.includes(
    CREATE_KEY_RESULT_PRIVILEGES.CREATE_KEY_RESULT_PARENT_MANDATORY
  );

  /* 
    if a kr can be created at root level (without parent), 
    this implicitly means canCreateChildKeyResult() is true.
  */
  const hasCreateKeyResultAtRootLevelPrivilege = accessPrivilegesList.includes(
    CREATE_KEY_RESULT_PRIVILEGES.CREATE_KEY_RESULT_PARENT_NON_MANDATORY
  );

  /* based on visibility enable viewing/ commenting on kr */
  const hasViewCommentOnKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(VIEW_COMMENT_KEY_RESULTS_PRIVILEGES.VIEW_COMMENT_PUBLIC_KEY_RESULTS)
      : accessPrivilegesList.includes(VIEW_COMMENT_KEY_RESULTS_PRIVILEGES.VIEW_COMMENT_ALL_KEY_RESULTS);

  /* based on visibility enable status and progress updates on kr */
  const hasUpdateKeyResultStatusAndProgressPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(
          UPDATE_KEY_RESULT_STATUS_PROGRESS_PRIVILEGES.UPDATE_PUBLIC_KEY_RESULT_STATUS_PROGRESS
        )
      : accessPrivilegesList.includes(
          UPDATE_KEY_RESULT_STATUS_PROGRESS_PRIVILEGES.UPDATE_ALL_KEY_RESULT_STATUS_PROGRESS
        );

  /* based on visibility enable editing of kr */
  const hasEditKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(EDIT_KEY_RESULT_PRIVILEGES.EDIT_PUBLIC_KEY_RESULT)
      : accessPrivilegesList.includes(EDIT_KEY_RESULT_PRIVILEGES.EDIT_ALL_KEY_RESULT);

  /* based on visibility enable delete option for a kr */
  const hasDeleteKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(DELETE_KEY_RESULT_PRIVILEGES.DELETE_PUBLIC_KEY_RESULT)
      : accessPrivilegesList.includes(DELETE_KEY_RESULT_PRIVILEGES.DELETE_ALL_KEY_RESULT);

  /* Objective Helpers */

  /* creation access for an objective */
  const hasCreateObjectivePrivilege =
    intersection(accessPrivilegesList, Object.keys(CREATE_OBJECTIVE_PRIVILEGES)).length > 0;

  /* based on visibility enable viewing/ commenting on objective */
  const hasViewCommentOnObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(VIEW_COMMENT_OBJECTIVE_PRIVILEGES.VIEW_COMMENT_PUBLIC_OBJECTIVES)
      : accessPrivilegesList.includes(VIEW_COMMENT_OBJECTIVE_PRIVILEGES.VIEW_COMMENT_ALL_OBJECTIVES);

  /* based on visibility enable status and progress updates on objective */
  const hasUpdateObjectiveStatusAndProgressPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(
          UPDATE_OBJECTIVE_STATUS_PROGRESS_PRIVILEGES.UPDATE_PUBLIC_OBJECTIVE_STATUS_PROGRESS
        )
      : accessPrivilegesList.includes(UPDATE_OBJECTIVE_STATUS_PROGRESS_PRIVILEGES.UPDATE_ALL_OBJECTIVE_STATUS_PROGRESS);
  /* based on visibility enable editing of objective */
  const hasEditObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(EDIT_OBJECTIVE_PRIVILEGES.EDIT_PUBLIC_OBJECTIVE)
      : accessPrivilegesList.includes(EDIT_OBJECTIVE_PRIVILEGES.EDIT_ALL_OBJECTIVE);

  /* based on visibility enable delete option for a objective */
  const hasDeleteObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(DELETE_OBJECTIVE_PRIVILEGES.DELETE_PUBLIC_OBJECTIVE)
      : accessPrivilegesList.includes(DELETE_OBJECTIVE_PRIVILEGES.DELETE_ALL_OBJECTIVE);

  /* UI specific permissions (actions common among objective and key result */

  const canDeleteOKR = type === OKR_TYPES.OBJECTIVE ? hasDeleteObjectivePrivilege : hasDeleteKeyResultPrivilege;

  const canEditOKR = type === OKR_TYPES.OBJECTIVE ? hasEditObjectivePrivilege : hasEditKeyResultPrivilege;

  const canUpdateOKRStatusAndProgress =
    type === OKR_TYPES.OBJECTIVE
      ? hasUpdateObjectiveStatusAndProgressPrivilege
      : hasUpdateKeyResultStatusAndProgressPrivilege;

  const canViewAndCommentOnOKR =
    type === OKR_TYPES.OBJECTIVE ? hasViewCommentOnObjectivePrivilege : hasViewCommentOnKeyResultPrivilege;

  /* 
    a user can only create an objective if he satisfies following conditions
      - OBJECTIVE_TYPE is not DISABLED, AND
      - current OKR is objective and user has objective creation privilege
      - current OKR is key result and KR cascading is enabled and user has objective creation privilege 
  */
  const canCreateObjective =
    !isObjectiveDisabled &&
    (type === OKR_TYPES.OBJECTIVE
      ? hasCreateObjectivePrivilege
      : IS_KR_CASCADING_ENABLED && hasCreateObjectivePrivilege);

  /* 
    a user can only create an key result if he satisfies following conditions 
      - current OKR is objective and, user has child key result creation privilege or root key result creation privilege
      - current OKR is key result and KR cascading is enabled and, user has child key result creation privilege 
        or root key result creation privilege
  */
  const canCreateKeyResult =
    type === OKR_TYPES.OBJECTIVE
      ? hasCreateChildKeyResultPrivilege || hasCreateKeyResultAtRootLevelPrivilege
      : IS_KR_CASCADING_ENABLED && (hasCreateChildKeyResultPrivilege || hasCreateKeyResultAtRootLevelPrivilege);

  /* other helper functions */

  const getObjectiveLevelList = () => {
    const objectiveLevels = [];
    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_COMPANY)) {
      objectiveLevels.push('COMPANY');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_DEPARTMENT)) {
      objectiveLevels.push('DEPARTMENT');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_TEAM)) {
      objectiveLevels.push('TEAM');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_INDIVIDUAL)) {
      objectiveLevels.push('INDIVIDUAL');
    }

    return objectiveLevels;
  };

  let canCloneObjective = false;
  let canCloneKeyResult = false;

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  if (isObjective) {
    const hasPermissionToCreateOKRWithThisOKRObjectiveLevel = getObjectiveLevelList().includes(objectiveLevel);
    switch (parentOKRType) {
      case OKR_TYPES.KEY_RESULT:
        canCloneObjective = IS_KR_CASCADING_ENABLED && hasPermissionToCreateOKRWithThisOKRObjectiveLevel;
        break;

      case OKR_TYPES.OBJECTIVE:
      case null:
        canCloneObjective = hasPermissionToCreateOKRWithThisOKRObjectiveLevel;
        break;
      default:
        canCloneObjective = false;
    }
  } else {
    switch (parentOKRType) {
      case OKR_TYPES.OBJECTIVE:
        canCloneKeyResult = hasCreateChildKeyResultPrivilege;
        break;
      case OKR_TYPES.KEY_RESULT:
        canCloneKeyResult = IS_KR_CASCADING_ENABLED && hasCreateChildKeyResultPrivilege;
        break;
      case null:
        canCloneKeyResult = IS_KR_CASCADING_ENABLED && hasCreateKeyResultAtRootLevelPrivilege;
        break;
      default:
        canCloneKeyResult = false;
    }
  }

  return {
    /* Key result permissions */
    hasCreateChildKeyResultPrivilege,
    hasCreateKeyResultAtRootLevelPrivilege,
    hasViewCommentOnKeyResultPrivilege,
    hasUpdateKeyResultStatusAndProgressPrivilege,
    hasEditKeyResultPrivilege,
    hasDeleteKeyResultPrivilege,

    /* Objective permissions */
    hasCreateObjectivePrivilege,
    hasViewCommentOnObjectivePrivilege,
    hasUpdateObjectiveStatusAndProgressPrivilege,
    hasEditObjectivePrivilege,
    hasDeleteObjectivePrivilege,

    /* Common permissions */

    canCreateObjective,
    canCreateKeyResult,
    canDeleteOKR,
    canEditOKR,
    canUpdateOKRStatusAndProgress,
    canViewAndCommentOnOKR,

    /* cloning permissions */
    canCloneKeyResult,
    canCloneObjective,

    /* other helpers */
    getObjectiveLevelList,
  };
};

export const getOKRAccessControlFromOKR = ({ okr = {}, OBJECTIVE_TYPE, IS_KR_CASCADING_ENABLED }) => {
  const accessPrivileges = okr?.accessPrivileges;
  const visibilityType = okr?.visibilityType;
  const type = okr?.type;
  const objectiveLevel = okr?.objectiveType;
  /* 
    The expectation here is that is okr is present, (i.e. ag-grid), we will set parentOKRType as undefined,
    this is done because we need to handle null and undefined separately, if null that means no parent, 
    if undefined that means don't show clone option all together.

    Ag grid is not sending parentPost info that is why we are doing things this way. once they send it we can
    show clone option in kebab menu
  */
  const parentOKRType = undefined;

  const isObjectiveDisabled = OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DISABLED.value;

  /* changing accessPrivilegesList to use accessPrivileges as it is as per api change */
  const accessPrivilegesList = Array.isArray(accessPrivileges) ? accessPrivileges : [];

  /* Key result helpers */

  /* if a child key result can be created. */
  const hasCreateChildKeyResultPrivilege = accessPrivilegesList.includes(
    CREATE_KEY_RESULT_PRIVILEGES.CREATE_KEY_RESULT_PARENT_MANDATORY
  );

  /* 
    if a kr can be created at root level (without parent), 
    this implicitly means canCreateChildKeyResult() is true.
  */
  const hasCreateKeyResultAtRootLevelPrivilege = accessPrivilegesList.includes(
    CREATE_KEY_RESULT_PRIVILEGES.CREATE_KEY_RESULT_PARENT_NON_MANDATORY
  );

  /* based on visibility enable viewing/ commenting on kr */
  const hasViewCommentOnKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(VIEW_COMMENT_KEY_RESULTS_PRIVILEGES.VIEW_COMMENT_PUBLIC_KEY_RESULTS)
      : accessPrivilegesList.includes(VIEW_COMMENT_KEY_RESULTS_PRIVILEGES.VIEW_COMMENT_ALL_KEY_RESULTS);

  /* based on visibility enable status and progress updates on kr */
  const hasUpdateKeyResultStatusAndProgressPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(
          UPDATE_KEY_RESULT_STATUS_PROGRESS_PRIVILEGES.UPDATE_PUBLIC_KEY_RESULT_STATUS_PROGRESS
        )
      : accessPrivilegesList.includes(
          UPDATE_KEY_RESULT_STATUS_PROGRESS_PRIVILEGES.UPDATE_ALL_KEY_RESULT_STATUS_PROGRESS
        );

  /* based on visibility enable editing of kr */
  const hasEditKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(EDIT_KEY_RESULT_PRIVILEGES.EDIT_PUBLIC_KEY_RESULT)
      : accessPrivilegesList.includes(EDIT_KEY_RESULT_PRIVILEGES.EDIT_ALL_KEY_RESULT);

  /* based on visibility enable delete option for a kr */
  const hasDeleteKeyResultPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(DELETE_KEY_RESULT_PRIVILEGES.DELETE_PUBLIC_KEY_RESULT)
      : accessPrivilegesList.includes(DELETE_KEY_RESULT_PRIVILEGES.DELETE_ALL_KEY_RESULT);

  /* Objective Helpers */

  /* creation access for an objective */
  const hasCreateObjectivePrivilege =
    intersection(accessPrivilegesList, Object.keys(CREATE_OBJECTIVE_PRIVILEGES)).length > 0;

  /* based on visibility enable viewing/ commenting on objective */
  const hasViewCommentOnObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(VIEW_COMMENT_OBJECTIVE_PRIVILEGES.VIEW_COMMENT_PUBLIC_OBJECTIVES)
      : accessPrivilegesList.includes(VIEW_COMMENT_OBJECTIVE_PRIVILEGES.VIEW_COMMENT_ALL_OBJECTIVES);

  /* based on visibility enable status and progress updates on objective */
  const hasUpdateObjectiveStatusAndProgressPrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(
          UPDATE_OBJECTIVE_STATUS_PROGRESS_PRIVILEGES.UPDATE_PUBLIC_OBJECTIVE_STATUS_PROGRESS
        )
      : accessPrivilegesList.includes(UPDATE_OBJECTIVE_STATUS_PROGRESS_PRIVILEGES.UPDATE_ALL_OBJECTIVE_STATUS_PROGRESS);
  /* based on visibility enable editing of objective */
  const hasEditObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(EDIT_OBJECTIVE_PRIVILEGES.EDIT_PUBLIC_OBJECTIVE)
      : accessPrivilegesList.includes(EDIT_OBJECTIVE_PRIVILEGES.EDIT_ALL_OBJECTIVE);

  /* based on visibility enable delete option for a objective */
  const hasDeleteObjectivePrivilege =
    visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList.includes(DELETE_OBJECTIVE_PRIVILEGES.DELETE_PUBLIC_OBJECTIVE)
      : accessPrivilegesList.includes(DELETE_OBJECTIVE_PRIVILEGES.DELETE_ALL_OBJECTIVE);

  /* UI specific permissions (actions common among objective and key result */

  const canDeleteOKR = type === OKR_TYPES.OBJECTIVE ? hasDeleteObjectivePrivilege : hasDeleteKeyResultPrivilege;

  const canEditOKR = type === OKR_TYPES.OBJECTIVE ? hasEditObjectivePrivilege : hasEditKeyResultPrivilege;

  const canUpdateOKRStatusAndProgress =
    type === OKR_TYPES.OBJECTIVE
      ? hasUpdateObjectiveStatusAndProgressPrivilege
      : hasUpdateKeyResultStatusAndProgressPrivilege;

  const canViewAndCommentOnOKR =
    type === OKR_TYPES.OBJECTIVE ? hasViewCommentOnObjectivePrivilege : hasViewCommentOnKeyResultPrivilege;

  /* 
    a user can only create an objective if he satisfies following conditions
      - OBJECTIVE_TYPE is not DISABLED, AND
      - current OKR is objective and user has objective creation privilege
      - current OKR is key result and KR cascading is enabled and user has objective creation privilege 
  */
  const canCreateObjective =
    !isObjectiveDisabled &&
    (type === OKR_TYPES.OBJECTIVE
      ? hasCreateObjectivePrivilege
      : IS_KR_CASCADING_ENABLED && hasCreateObjectivePrivilege);

  /* 
    a user can only create an key result if he satisfies following conditions 
      - current OKR is objective and, user has child key result creation privilege or root key result creation privilege
      - current OKR is key result and KR cascading is enabled and, user has child key result creation privilege 
        or root key result creation privilege
  */
  const canCreateKeyResult =
    type === OKR_TYPES.OBJECTIVE
      ? hasCreateChildKeyResultPrivilege || hasCreateKeyResultAtRootLevelPrivilege
      : IS_KR_CASCADING_ENABLED && (hasCreateChildKeyResultPrivilege || hasCreateKeyResultAtRootLevelPrivilege);

  /* other helper functions */

  const getObjectiveLevelList = () => {
    const objectiveLevels = [];
    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_COMPANY)) {
      objectiveLevels.push('COMPANY');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_DEPARTMENT)) {
      objectiveLevels.push('DEPARTMENT');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_TEAM)) {
      objectiveLevels.push('TEAM');
    }

    if (accessPrivilegesList.includes(CREATE_OBJECTIVE_PRIVILEGES.CREATE_OBJECTIVE_INDIVIDUAL)) {
      objectiveLevels.push('INDIVIDUAL');
    }

    return objectiveLevels;
  };

  let canCloneObjective = false;
  let canCloneKeyResult = false;

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  if (isObjective) {
    const hasPermissionToCreateOKRWithThisOKRObjectiveLevel = getObjectiveLevelList().includes(objectiveLevel);
    switch (parentOKRType) {
      case OKR_TYPES.KEY_RESULT:
        canCloneObjective = IS_KR_CASCADING_ENABLED && hasPermissionToCreateOKRWithThisOKRObjectiveLevel;
        break;

      case OKR_TYPES.OBJECTIVE:
      case null:
        canCloneObjective = hasPermissionToCreateOKRWithThisOKRObjectiveLevel;
        break;
      default:
        canCloneObjective = false;
    }
  } else {
    switch (parentOKRType) {
      case OKR_TYPES.OBJECTIVE:
        canCloneKeyResult = hasCreateChildKeyResultPrivilege;
        break;
      case OKR_TYPES.KEY_RESULT:
        canCloneKeyResult = IS_KR_CASCADING_ENABLED && hasCreateChildKeyResultPrivilege;
        break;
      case null:
        canCloneKeyResult = IS_KR_CASCADING_ENABLED && hasCreateKeyResultAtRootLevelPrivilege;
        break;
      default:
        canCloneKeyResult = false;
    }
  }

  return {
    /* Key result permissions */
    hasCreateChildKeyResultPrivilege,
    hasCreateKeyResultAtRootLevelPrivilege,
    hasViewCommentOnKeyResultPrivilege,
    hasUpdateKeyResultStatusAndProgressPrivilege,
    hasEditKeyResultPrivilege,
    hasDeleteKeyResultPrivilege,

    /* Objective permissions */
    hasCreateObjectivePrivilege,
    hasViewCommentOnObjectivePrivilege,
    hasUpdateObjectiveStatusAndProgressPrivilege,
    hasEditObjectivePrivilege,
    hasDeleteObjectivePrivilege,

    /* Common permissions */

    canCreateObjective,
    canCreateKeyResult,
    canDeleteOKR,
    canEditOKR,
    canUpdateOKRStatusAndProgress,
    canViewAndCommentOnOKR,

    /* cloning permissions */
    canCloneKeyResult,
    canCloneObjective,

    /* other helpers */
    getObjectiveLevelList,
  };
};
